import { Filter } from '@components/chat/Filter'
import type { MessageType } from '@components/chat/types'
import { Filters } from '@components/utils/sendQuery'
import { usePostUploadFiles } from '@features/upload-files/hooks/usePostUploadFiles'
import AttachFile from '@mui/icons-material/AttachFile'
import Send from '@mui/icons-material/Send'
import Stop from '@mui/icons-material/Stop'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import { Dispatch, SetStateAction, useRef, useState } from 'react'
import { AttachedFile, AttachedFiles } from './AttachedFiles'

type Props = {
  addMessage: (message: MessageType) => void
  setFilters: Dispatch<SetStateAction<Filters>>
  filters: Filters
  chatId?: string
  disabled?: boolean
  isLoading?: boolean
  onStop?: () => void
}

export const Input = ({ addMessage, filters, setFilters, chatId, disabled, isLoading, onStop }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<AttachedFile[]>([])
  const upload = usePostUploadFiles()

  const handleSend = () => {
    if (disabled || isLoading) return
    if (inputRef.current && inputRef.current.value?.length > 0) {
      addMessage({
        query: inputRef.current?.value,
        files: files
          .filter((file) => file.id)
          .map((file) => ({
            type: file.type,
            file_id: file.id!,
          })),
        type: 'user',
        created_at: new Date().toISOString(),
      })
      inputRef.current.value = ''
      setFiles([])
    }
  }

  const handleAttachFile = () => {
    if (disabled || isLoading) return
    // Trigger file input dialog
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.multiple = true
    fileInput.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement
      if (target.files) {
        const selectedFiles = Array.from(target.files)

        // Filter out files that are already in the list
        const filesToUpload = selectedFiles.filter((file) => !files.find((f) => f.name === file.name))

        if (filesToUpload.length === 0) return

        const newFiles = filesToUpload.map((file) => ({
          name: file.name,
          size: file.size,
          type: file.type,
          progress: 0,
        }))

        setFiles((prev) => [...prev, ...newFiles])

        // Upload files in parallel with max concurrency of 3
        const uploadPromises: Promise<void>[] = []
        const maxConcurrency = 3

        for (let i = 0; i < filesToUpload.length; i += maxConcurrency) {
          const batch = filesToUpload.slice(i, i + maxConcurrency)

          const batchPromises = batch.map(async (file) => {
            const formData = new FormData()
            formData.append('file', file)

            return new Promise<void>((resolve) => {
              upload.mutate({
                formData,
                onUploadProgress: (progress) => {
                  console.log('Upload progress:', progress)
                  setFiles((prev) => [
                    ...prev.map((f) => {
                      console.log('updating', f, file, progress)
                      if (f.name === file.name) {
                        return {
                          ...f,
                          progress: progress.progress,
                        }
                      }
                      return f
                    }),
                  ])
                },
                onError: () => {
                  // Remove file that failed to upload
                  setFiles((prev) => prev.filter((f) => f.name !== file.name))
                  resolve()
                },
                onSuccess: (res) => {
                  // Add "id" and "preview_url" to uploaded file
                  setFiles((prev) =>
                    prev.map((f) => {
                      if (f.name === file.name) {
                        return {
                          ...f,
                          preview_url: res.data.preview_url ?? res.data.source_url,
                          id: res.data.id,
                          progress: 100,
                        }
                      }
                      return f
                    })
                  )
                  resolve()
                },
                href: `/conversations/upload`,
              })
            })
          })

          uploadPromises.push(...batchPromises)
          await Promise.all(batchPromises)
        }
      }
    }
    fileInput.click()
  }

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Filter
          filters={filters}
          setFilters={setFilters}
          disabled={!!chatId || disabled}
        />
        <AttachedFiles
          files={files}
          setFiles={setFiles}
        />
      </Box>
      <TextField
        disabled={disabled || isLoading}
        multiline
        fullWidth
        autoFocus
        variant="outlined"
        color="primary"
        focused
        inputRef={inputRef}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            event.stopPropagation()
            handleSend()
          }
        }}
        slotProps={{
          input: {
            endAdornment: (
              <>
                <IconButton
                  color="primary"
                  onClick={handleAttachFile}
                  disabled={disabled || isLoading}
                >
                  <AttachFile />
                </IconButton>
                {isLoading ? (
                  <IconButton
                    color="secondary"
                    onClick={onStop}
                    disabled={disabled}
                    title="Stop response"
                  >
                    <Stop />
                  </IconButton>
                ) : (
                  <IconButton
                    color="primary"
                    onClick={handleSend}
                    disabled={disabled}
                  >
                    <Send />
                  </IconButton>
                )}
              </>
            ),
          },
        }}
      />
    </Box>
  )
}
